import { ScrollbarContainer, StyledText } from 'UI';
import api from 'api';
import { CourseDto, CourseDtoTypeEnum, PaymentDtoTypeEnum } from 'api/generated';
import { Loader } from 'components';
import { resolveBundleForPicks, useBundleBuilderCatalog } from 'components/Modals/BundleBuilderModal';
import {
  ownedCertIdsFrom,
  useBundleBuilderContext,
} from 'components/Modals/BundleBuilderModal/useBundleBuilderContext';
import { routes } from 'constant/routes';
import { useAsyncAction, useBundleUpsell, useNotifications } from 'hooks';
import LightLayout from 'layouts/LightLayout';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { typography } from 'styles/general/typography';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import { BundleBuilderPanel, BundleBuilderPanelSkeleton } from './components/BundleBuilderPanel';
import { CourseCard } from './components/CourseCard';
import { useMiniBundleBuilder } from './useMiniBundleBuilder';

/** Large enough that a direction's whole catalogue arrives in one request; categories need all of it. */
const PAGE_SIZE = 100;

type Category = 'all' | 'bundles' | 'certifications' | 'study';

const PAID_TYPES: string[] = [
  PaymentDtoTypeEnum.Purchase,
  PaymentDtoTypeEnum.Subscription,
  PaymentDtoTypeEnum.Installment,
];

const isPaid = (course: CourseDto) => PAID_TYPES.includes(course.currentPayment?.type ?? '');

const isTrial = (course: CourseDto) => course.currentPayment?.type === PaymentDtoTypeEnum.Free;

const CATEGORY_LABEL: Record<Category, string> = {
  all: 'All',
  bundles: 'Bundles',
  certifications: 'Certifications',
  study: 'Study systems',
};

const AddNewCourse = () => {
  const dispatch = useAppDispatch();
  const { errorToast } = useNotifications();
  const { handleUpgradeBundle } = useBundleUpsell();
  const accessToken = useAppSelector(selectors.auth.selectAccessToken);
  // Local, not the shared `courses` slot: CourseProvider refetches the learner's OWN courses into that
  // slot on every load and token change, so on a hard refresh it landed after this page's request
  // and the page listed what the learner already has instead of what they can add.
  const [courses, setCourses] = useState<CourseDto[] | null>(null);

  const [getCourseByIdAction] = useAsyncAction(actions.courses.getCourseById);
  const [isCoursesLoading, setIsCoursesLoading] = useState(true);

  const { catalog, error: catalogError } = useBundleBuilderCatalog(true);
  const { context, error: contextError } = useBundleBuilderContext(true);

  const [category, setCategory] = useState<Category>('all');
  const [isContinuing, setIsContinuing] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);
  const panelRef = useRef<HTMLElement>(null);

  const getToken = useCallback(
    () =>
      accessToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      '',
    [accessToken],
  );

  // Every page up front rather than infinite scroll: a course can only be filed under its category
  // once the whole list is known, and a direction sells a few dozen courses at most.
  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;

    const loadCourses = async () => {
      setIsCoursesLoading(true);
      try {
        const items: CourseDto[] = [];
        let page = 1;
        let totalPages = 1;
        do {
          // Not withoutActivated: that drops everything on the dashboard, trials included, so a CPT
          // trialist never saw CPT here. Paid courses are filtered out below instead.
          const { data, status } = await api.Courses.coursesControllerFind(page, PAGE_SIZE, false);
          if (status > 399) throw data;
          items.push(...data.items);
          totalPages = data.meta?.totalPages ?? 1;
          page += 1;
        } while (page <= totalPages);

        if (!cancelled) setCourses(items);
      } catch {
        if (!cancelled) errorToast('Something went wrong');
      } finally {
        if (!cancelled) setIsCoursesLoading(false);
      }
    };

    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // The builder waits for the learner's context so it can open on their trial bundle; a failed
  // context request still lets it open, just unseeded.
  const isCatalogPending = catalog === null && catalogError === null;
  const isContextPending = context === null && contextError === null;
  const showBuilder = Boolean(catalog?.bundles.length) && !context?.ownsEverything;

  const seed = useMemo(
    () =>
      context?.trialBundle?.tier
        ? { tier: context.trialBundle.tier, memberSlugs: context.trialBundle.memberSlugs }
        : null,
    [context],
  );
  const builder = useMiniBundleBuilder(isContextPending ? null : catalog, seed);
  const ownedCertIds = useMemo(() => ownedCertIdsFrom(context), [context]);

  // Bundles are sold only through the builder panel, so bundle rows never reach the grid — the
  // builder packages and the legacy ones (CPT + CNS etc.) alike.
  const groups = useMemo(() => {
    // Hide what the learner has paid for — directly (currentPayment) or through a paid bundle
    // (certLevels, which the course list does not see). Trials stay: they are still to be bought.
    const list = (courses ?? []).filter((course) => !isPaid(course) && !ownedCertIds.includes(course.id));

    return {
      certifications: list.filter((course) => course.type === CourseDtoTypeEnum.Certificate),
      study: list.filter(
        (course) => course.type !== CourseDtoTypeEnum.Bundle && course.type !== CourseDtoTypeEnum.Certificate,
      ),
    };
  }, [courses, ownedCertIds]);

  const counts: Record<Exclude<Category, 'all'>, number> = {
    bundles: showBuilder ? builder.tiers.length : 0,
    certifications: groups.certifications.length,
    study: groups.study.length,
  };

  const presentCategories = (Object.keys(counts) as Exclude<Category, 'all'>[]).filter((key) => counts[key] > 0);
  const showTabs = presentCategories.length > 1;
  const isVisible = (key: Exclude<Category, 'all'>) => category === 'all' || category === key;

  // If a category empties (e.g. the last study system was bought elsewhere), fall back to All.
  useEffect(() => {
    if (category !== 'all' && !presentCategories.includes(category)) setCategory('all');
  }, [category, presentCategories]);

  const handleGet = (course: CourseDto) => {
    trackAmplitudeEvent(`Get ${course.name} Pressed`);
    handleUpgradeBundle(course);
  };

  const scrollToPanel = () => {
    // After the category switch has rendered the panel.
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const handleAddToBundle = (course: CourseDto) => {
    trackAmplitudeEvent('Bundle And Save Pressed', { course: course.name, source: 'add_new_course' });
    builder.focusCert(course.id);
    setContinueError(null);
    if (category !== 'all' && category !== 'bundles') setCategory('all');
    scrollToPanel();
  };

  /**
   * Same handoff as the full builder's Continue (CourseProvider.handleBundleBuilderContinue): resolve
   * the exact pick set on the server, load that bundle, then go straight to installments-or-lifetime.
   */
  const handleContinue = async () => {
    if (!builder.isComplete || !builder.active) return;

    trackAmplitudeEvent('Bundle Continue Pressed', { tier: builder.active.tier, source: 'add_new_course' });
    setIsContinuing(true);
    setContinueError(null);

    try {
      const bundleId = await resolveBundleForPicks(builder.selectedIds, getToken());
      if (!bundleId) {
        setContinueError('That combination is not sold as a package. Try a different selection.');
        return;
      }

      const bundle = await getCourseByIdAction(bundleId);
      dispatch(actions.courses.setDisabledStatus(false));
      dispatch(actions.courses.selectCourse(bundle));
      dispatch(actions.courses.openBundlePaymentChoiceModal());
    } catch (e) {
      setContinueError(e instanceof Error ? e.message : 'Could not continue. Please try again.');
    } finally {
      setIsContinuing(false);
    }
  };

  const handleOpenFullBuilder = () => {
    const sourceCourseId = builder.active?.primaryCourseId ?? builder.selectedIds[0];
    if (!sourceCourseId) return;

    trackAmplitudeEvent('Full Bundle Builder Opened', { source: 'add_new_course' });
    dispatch(
      actions.courses.openBundleBuilderModal({
        sourceCourseId,
        seed: builder.active
          ? {
              tier: builder.active.tier,
              memberSlugs: builder.selectedIds
                .map((id) => builder.certById.get(id)?.slug)
                .filter((slug): slug is string => Boolean(slug)),
            }
          : null,
        ownedCertIds,
      }),
    );
  };

  const blockedReason = context?.activeInstallmentBundle
    ? `You're currently paying off ${context.activeInstallmentBundle.name}. To change your package while a payment plan is running, please contact support so your plan can be adjusted first.`
    : null;

  const isBuilderPending = isCatalogPending || (Boolean(catalog?.bundles.length) && isContextPending);
  const hasAnything = showBuilder || groups.certifications.length + groups.study.length > 0;

  return (
    <StyledLightLayout
      breadcrumbsProps={{
        routeName: routes.profile,
        pageName: 'Profile',
        title: 'Add new course',
      }}>
      <Scroll>
        <Content>
          {showTabs && (
            <CategoryBar role="tablist" aria-label="Course categories">
              {(['all', ...presentCategories] as Category[]).map((key) => (
                <CategoryChip
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={category === key}
                  $active={category === key}
                  onClick={() => setCategory(key)}>
                  {CATEGORY_LABEL[key]}
                  {key !== 'all' && <Count $active={category === key}>{counts[key]}</Count>}
                </CategoryChip>
              ))}
            </CategoryBar>
          )}

          {isVisible('bundles') && (isBuilderPending || showBuilder) && (
            <Section aria-label="Bundles">
              {isBuilderPending && <BundleBuilderPanelSkeleton />}
              {!isBuilderPending && showBuilder && catalog && (
                <BundleBuilderPanel
                  ref={panelRef}
                  builder={builder}
                  catalog={catalog}
                  ownedCertIds={ownedCertIds}
                  trialTier={context?.trialBundle?.tier ?? null}
                  blockedReason={blockedReason}
                  isContinuing={isContinuing}
                  error={continueError}
                  onContinue={handleContinue}
                  onOpenFullBuilder={handleOpenFullBuilder}
                />
              )}
            </Section>
          )}

          {isVisible('certifications') && groups.certifications.length > 0 && (
            <Section aria-labelledby="add-course-certifications">
              {showTabs && (
                <SectionHeader>
                  <SectionTitle id="add-course-certifications">Certifications</SectionTitle>
                  {showBuilder && <SectionCaption>Each one is also available in the bundles above.</SectionCaption>}
                </SectionHeader>
              )}
              <Grid>
                {groups.certifications.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    isTrial={isTrial(course)}
                    onGet={handleGet}
                    onAddToBundle={
                      showBuilder && !blockedReason && builder.certById.has(course.id)
                        ? () => handleAddToBundle(course)
                        : undefined
                    }
                  />
                ))}
              </Grid>
            </Section>
          )}

          {isVisible('study') && groups.study.length > 0 && (
            <Section aria-labelledby="add-course-study">
              {showTabs && (
                <SectionHeader>
                  <SectionTitle id="add-course-study">Study systems</SectionTitle>
                  <SectionCaption>Study guides, practice questions and flashcards for your exam.</SectionCaption>
                </SectionHeader>
              )}
              <Grid>
                {groups.study.map((course) => (
                  <CourseCard key={course.id} course={course} isTrial={isTrial(course)} onGet={handleGet} />
                ))}
              </Grid>
            </Section>
          )}

          {isCoursesLoading && !courses?.length && (
            <Centered>
              <Loader />
            </Centered>
          )}

          {!isCoursesLoading && !isBuilderPending && !hasAnything && (
            <Centered>
              <StyledText>You&apos;ve added every course in this career.</StyledText>
            </Centered>
          )}
        </Content>
      </Scroll>
    </StyledLightLayout>
  );
};

export default AddNewCourse;

const StyledLightLayout = styled(LightLayout)`
  ${({ theme: { colors } }) => css`
    /* Full-bleed so the scrollbar sits at the window edge; Content keeps the reading width. */
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0;

    ${respondToWidth.sm`
      background-color: ${colors.neutrals[10]};
    `}
  `}
`;

const Scroll = styled(ScrollbarContainer)`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 24px 40px 48px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${respondToWidth.ls`
    padding: 24px 24px 40px;
  `}

  ${respondToWidth.sm`
    padding: 12px 16px 32px;
    gap: 24px;
  `}
`;

const CategoryBar = styled.div`
  ${({ theme: { colors } }) => css`
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    gap: 8px;
    margin: -24px -4px -16px;
    padding: 24px 4px 12px;
    overflow-x: auto;
    scrollbar-width: none;
    background: ${colors.neutrals[10]};

    &::-webkit-scrollbar {
      display: none;
    }

    ${respondToWidth.sm`
      margin: -12px -16px -8px;
      padding: 12px 16px 10px;
    `}
  `}
`;

const CategoryChip = styled.button<{ $active: boolean }>`
  ${({ theme: { colors }, $active }) => css`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 999px;
    cursor: pointer;
    ${typography.body_basic_semibold_14};
    color: ${$active ? colors.neutrals[11] : colors.neutrals[1]};
    background: ${$active ? colors.neutrals[1] : colors.neutrals[11]};
    border: 1px solid ${$active ? colors.neutrals[1] : colors.neutrals[8]};
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover {
      border-color: ${$active ? colors.neutrals[1] : colors.neutrals[6]};
    }
  `}
`;

const Count = styled.span<{ $active: boolean }>`
  ${({ theme: { colors }, $active }) => css`
    min-width: 20px;
    padding: 0 6px;
    border-radius: 999px;
    text-align: center;
    ${typography.footnote_bold_12};
    line-height: 20px;
    color: ${$active ? colors.neutrals[1] : colors.neutrals[2]};
    background: ${$active ? colors.neutrals[11] : colors.neutrals[9]};
  `}
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  ${typography.title_5_bold_20};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const SectionCaption = styled.p`
  margin: 0;
  ${typography.body_basic_regular_14};
  color: ${({ theme: { colors } }) => colors.neutrals[3]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  ${respondToWidth.s`
    grid-template-columns: 1fr;
  `}
`;

const Centered = styled.div`
  width: 100%;
  min-height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
